import React, { useCallback, useEffect, useState } from "react";
import { BellRing, History, Printer, Send } from "lucide-react";
import { euro, practiceApi } from "../../lib/practiceApi";
import { SortHeadCell } from "../sorting/SortableTh";
import { sortRows, useSortPreference } from "../../lib/sorting";

const RECEIVABLE_COLUMNS = [
  ["client_name", "committente", "text"], ["agreed", "concordato", "number"], ["collected", "incassato", "number"],
  ["outstanding", "scoperto", "number"], ["days_since_payment", "giorni", "number"], ["last_reminder_at", "promemoria", "date"],
];
const DEFAULT_SORT = { key: "outstanding", kind: "number", dir: "desc" };
import { DocumentButton } from "../DocumentButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

async function openDocument(path, filename, fmt = "pdf") {
  const response = await fetch(`${API}${path}`, { credentials: "include" });
  if (!response.ok) throw new Error("Documento non disponibile");
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement("a");
  link.href = url;
  if (fmt === "pdf") { link.target = "_blank"; link.rel = "noopener noreferrer"; }
  else link.download = filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(url), 60000);
}

const CHANNELS = { pdf: "PDF generato", email: "Email", pec: "PEC", phone: "Telefono", whatsapp: "WhatsApp" };
const stamp = value => String(value || "").slice(0, 16).replace("T", " alle ");

export const ReceivablesPanel = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const { sort, onSort } = useSortPreference("receivables", DEFAULT_SORT);
  const [error, setError] = useState("");
  const [openHistory, setOpenHistory] = useState("");
  const [sending, setSending] = useState("");
  const [flash, setFlash] = useState("");

  const load = useCallback(
    () => practiceApi(`/finance/receivables?days=${days}`).then(setData).catch(err => setError(err.message)),
    [days]);
  useEffect(() => { load(); }, [load]);

  async function sendReminder(row) {
    setSending(row.client_key); setError(""); setFlash("");
    try {
      const outcome = await practiceApi(`/finance/receivables/${encodeURIComponent(row.client_key)}/send-reminder?days=${days}`, {
        method: "POST", body: JSON.stringify({ email: "", note: "" }),
      });
      setFlash(outcome.message);
      setTimeout(() => setFlash(""), 8000);
      load();
    } catch (err) { setError(err.message); } finally { setSending(""); }
  }

  const ordered = sortRows(data?.clients || [], sort?.key ? sort : DEFAULT_SORT,
    (row, key) => key === "last_reminder_at" ? (row.last_reminder?.sent_at || "") : row[key]);
  return <section className="receivables-card" data-testid="receivables-panel">
    <header>
      <div>
        <span className="finance-kicker"><BellRing size={13} />SOLLECITI INCASSI</span>
        <h2>Committenti con somme scoperte</h2>
      </div>
      <label className="receivables-filter">Giorni dall'ultimo incasso oltre
        <input type="number" min="0" step="5" data-testid="receivables-days" value={days} onChange={e => setDays(Number(e.target.value) || 0)} />
      </label>
    </header>

    {error && <div className="error" role="alert" data-testid="receivables-error">{error}</div>}
    {flash && <div className="flash" data-testid="receivables-flash">{flash}</div>}
    {!data ? <p className="muted">Calcolo delle posizioni aperte…</p> : !data.clients.length
      ? <p className="muted" data-testid="receivables-empty">Nessuna posizione scoperta oltre {days} giorni. Tutto incassato.</p>
      : <>
        <div className="receivables-total" data-testid="receivables-total">
          <span>{data.totals.clients} committenti · {data.totals.practices} pratiche</span>
          <strong>{euro(data.totals.outstanding)} da incassare</strong>
        </div>
        <div className="data-table">
          <div className="table-head" style={{ gridTemplateColumns: "1.4fr .85fr .85fr .85fr .5fr 1fr 330px" }}>
            {RECEIVABLE_COLUMNS.map(([key, label, kind]) => <SortHeadCell key={key} label={label} sortKey={key} kind={kind}
              sort={sort} onSort={onSort} testId={`receivable-sort-${key}`} />)}
          </div>
          {ordered.map((row, index) => <React.Fragment key={row.client_key}><div className="table-row" data-testid={`receivable-row-${index}`} style={{ gridTemplateColumns: "1.4fr .85fr .85fr .85fr .5fr 1fr 330px" }}>
            <span><strong>{row.client_name}</strong><small className="receivables-practices">{row.practices.map(p => p.title).join(" · ")}</small></span>
            <span>{euro(row.agreed)}</span>
            <span>{euro(row.collected)}</span>
            <span className="receivables-out">{euro(row.outstanding)}</span>
            <span>{row.days_since_payment}</span>
            <span className="receivables-last" data-testid={`receivable-last-${index}`}>
              {row.last_reminder ? <>{stamp(row.last_reminder.sent_at)}<small>{CHANNELS[row.last_reminder.channel] || row.last_reminder.channel} · {row.reminders_count} invii</small></> : <em>mai sollecitato</em>}
            </span>
            <span className="receivables-actions">
              <DocumentButton label="Genera promemoria" testId={`receivable-reminder-${index}`} icon={<Printer size={14} />}
                onGenerate={async fmt => { await openDocument(`/finance/receivables/${encodeURIComponent(row.client_key)}/pdf?days=${days}&fmt=${fmt}`, `Promemoria ${row.client_name}.${fmt}`, fmt).catch(err => setError(err.message)); load(); }} />
              <button className="primary sollecito" title="Invia il sollecito via email e metti le pratiche in attesa cliente"
                disabled={sending === row.client_key} data-testid={`receivable-send-${index}`} onClick={() => sendReminder(row)}>
                <Send size={13} />{sending === row.client_key ? "Invio…" : "Invia sollecito"}
              </button>
              <button className="icon-btn" title="Storico solleciti" data-testid={`receivable-history-${index}`} onClick={() => setOpenHistory(openHistory === row.client_key ? "" : row.client_key)}><History size={14} /></button>
            </span>
          </div>
          {openHistory === row.client_key && <div className="receivables-history" data-testid={`receivable-history-panel-${index}`}>
            {row.reminders.length ? row.reminders.map(entry => <div key={entry.id}>
              <strong>{stamp(entry.sent_at)}</strong>
              <span>{CHANNELS[entry.channel] || entry.channel}{entry.note ? ` · ${entry.note}` : ""}</span>
              <em>{entry.by_user_name}</em>
            </div>) : <p className="muted">Nessun sollecito registrato per questo committente.</p>}
          </div>}
          </React.Fragment>)}
        </div>
      </>}
  </section>;
};

export const MonthlyReportButton = () => {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [error, setError] = useState("");
  return <div className="monthly-report" data-testid="monthly-report">
    <input type="month" data-testid="monthly-report-month" value={month} onChange={e => setMonth(e.target.value)} />
    <DocumentButton label="Genera report mensile" testId="monthly-report-generate" icon={<Printer size={15} />}
      onGenerate={fmt => openDocument(`/finance/report/monthly?month=${month}&fmt=${fmt}`, `Report ${month}.${fmt}`, fmt).catch(err => setError(err.message))} />
    {error && <small className="monthly-report-error">{error}</small>}
  </div>;
};
